import axios, { AxiosResponse } from 'axios'
import { AnexoType, ID } from '../../../../../../_metronic/helpers'
import {
  ArquivoDto,
  AtendimentoDto,
  AtendimentoDtoCreate,
  AtendimentoView,
} from '../../../atendimento/core/_models'
import { FormularioEnvioDto } from '../../../formularios/formularios-create/core/_models'
import { LotacaoView } from '../../../funcionarios/funcionarios-list/core/_lotacaoModels'
import { FileDownloadResponse } from '../../../relatorios/relatorio-list/core/_models'
import {
  AnexoInterconsulta,
  DesfechoInterconsultaDtoCreate,
  DesfechoInterconsultaView,
  InterconsultasSolicitanteQueryResponse,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const INTERCONSULTA_URL = `${API_URL}/atendimentos/interconsulta`

/** Interconsultas criadas pelo usuário logado, com situação e prazos de réplica/desfecho. */
export const getInterconsultasSolicitante = (
  query: string
): Promise<InterconsultasSolicitanteQueryResponse> =>
  axios
    .get(`${INTERCONSULTA_URL}/solicitante?${query}`)
    .then((resp: AxiosResponse<InterconsultasSolicitanteQueryResponse>) => resp.data)

/**
 * Interconsultas da equipe: todas as da empresa, de qualquer solicitante (ex.: acompanhar a de um
 * colega que saiu do plantão). Mesma linha da lista do solicitante.
 */
export const getInterconsultasEquipe = (
  query: string
): Promise<InterconsultasSolicitanteQueryResponse> =>
  axios
    .get(`${INTERCONSULTA_URL}/equipe?${query}`)
    .then((resp: AxiosResponse<InterconsultasSolicitanteQueryResponse>) => resp.data)

/** CSV das interconsultas do solicitante logado (a rota já restringe a tipo e solicitante). */
export const getCsvInterconsultasSolicitante = (query: string): Promise<FileDownloadResponse> =>
  axios
    .get(`${INTERCONSULTA_URL}/solicitante/csv?${decodeURIComponent(query)}`)
    .then((resp: AxiosResponse<FileDownloadResponse>) => resp.data)

/**
 * Abre a interconsulta para o paciente. Sem vínculo ativo de solicitante ou profissional de saúde
 * na instituição do paciente, a API devolve 422 com a mensagem para o usuário.
 */
export const abrirInterconsulta = (
  idPaciente: number,
  atendimento: AtendimentoDtoCreate
): Promise<AtendimentoDto> =>
  axios
    .post(`${INTERCONSULTA_URL}/${idPaciente}`, atendimento)
    .then((resp: AxiosResponse<AtendimentoDto>) => resp.data)

/** Envia o formulário de solicitação. A rota recusa o formulário de parecer (do teleconsultor). */
export const enviarFormularioSolicitacao = (
  atendimentoId: ID,
  idFormularioAtendimento: ID,
  envio: FormularioEnvioDto
): Promise<AtendimentoDto> =>
  axios
    .post(
      `${INTERCONSULTA_URL}/${atendimentoId}/formulario/${idFormularioAtendimento}/enviar`,
      envio
    )
    .then((resp: AxiosResponse<AtendimentoDto>) => resp.data)

/** Lotações do usuário logado como solicitante na empresa do paciente (não exige Funcionario:read). */
export const getLotacoesSolicitante = (idEmpresa: number): Promise<LotacaoView[]> =>
  axios
    .get(`${INTERCONSULTA_URL}/solicitante/lotacoes`, { params: { empresa: idEmpresa } })
    .then((resp: AxiosResponse<LotacaoView[]>) => resp.data)

/**
 * Corrige a lotação do solicitante (interconsultas antigas sem lotação). A lotação tem de estar
 * vigente na data da solicitação; `lotacaoId` vai como query param, como na correção da lotação do
 * responsável. Mesma permissão dela (`Atendimento:corrigirLotacao`).
 */
export const corrigirLotacaoSolicitante = (
  atendimentoId: ID,
  lotacaoId: number
): Promise<AtendimentoView> =>
  axios
    .patch(`${INTERCONSULTA_URL}/${atendimentoId}/lotacao-solicitante`, null, {
      params: { lotacaoId },
    })
    .then((resp: AxiosResponse<AtendimentoView>) => resp.data)

/** Réplica do solicitante ao parecer: devolve a interconsulta ao teleconsultor (situação EM_REPLICA). */
export const registrarReplica = (atendimentoId: ID, observacao: string): Promise<AtendimentoDto> =>
  axios
    .post(`${INTERCONSULTA_URL}/${atendimentoId}/replica`, { observacao })
    .then((resp: AxiosResponse<AtendimentoDto>) => resp.data)

/** Desfecho da interconsulta: encerra o ciclo (situação AVALIADA). */
export const registrarDesfecho = (
  atendimentoId: ID,
  desfecho: DesfechoInterconsultaDtoCreate
): Promise<DesfechoInterconsultaView> =>
  axios
    .post(`${INTERCONSULTA_URL}/${atendimentoId}/desfecho`, desfecho)
    .then((resp: AxiosResponse<DesfechoInterconsultaView>) => resp.data)

/**
 * Anexos da solicitação (exames do paciente). Rotas próprias da interconsulta: as genéricas
 * /atendimentos/anexo exigem `Atendimento:*`, que o perfil solicitante não tem. Escrita só com a
 * interconsulta aguardando o teleconsultor (fora disso, 422); remover, só o anexo que o próprio
 * usuário enviou (senão 403).
 */
export const getAnexosInterconsulta = (atendimentoId: ID): Promise<AnexoInterconsulta[]> =>
  axios
    .get(`${INTERCONSULTA_URL}/${atendimentoId}/anexos`)
    .then((resp: AxiosResponse<AnexoInterconsulta[]>) => resp.data)

/** Anexa um arquivo como exame. Devolve a lista de anexos atualizada. */
export const enviarAnexoInterconsulta = (
  atendimentoId: ID,
  arquivo: File,
  descricao?: string
): Promise<AnexoInterconsulta[]> => {
  const formData = new FormData()
  formData.append('dto', JSON.stringify({ tipo: AnexoType.Exame, descricao: descricao || null }))
  formData.append('file', arquivo)
  return axios
    .put(`${INTERCONSULTA_URL}/${atendimentoId}/anexo/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((resp: AxiosResponse<AnexoInterconsulta[]>) => resp.data)
}

/** Remove um anexo enviado pelo próprio usuário. Devolve a lista de anexos atualizada. */
export const removerAnexoInterconsulta = (
  atendimentoId: ID,
  idAnexo: ID
): Promise<AnexoInterconsulta[]> =>
  axios
    .delete(`${INTERCONSULTA_URL}/${atendimentoId}/anexo/${idAnexo}`)
    .then((resp: AxiosResponse<AnexoInterconsulta[]>) => resp.data)

/** Conteúdo do anexo (ou só a URL assinada), para baixar. */
export const baixarAnexoInterconsulta = (atendimentoId: ID, idAnexo: ID): Promise<ArquivoDto> =>
  axios
    .get(`${INTERCONSULTA_URL}/${atendimentoId}/anexo/${idAnexo}`, {
      params: { somenteUrl: false },
    })
    .then((resp: AxiosResponse<ArquivoDto>) => resp.data)
